import { resolveAddressToDomain, resolveDomainToAddress } from '@azns/resolver-core';
import { ApiPromise } from '@polkadot/api';
import { isEthereumAddress, throwAfter } from '@talismn/util';
import anylogger from 'anylogger';

var packageJson = {
	name: "@talismn/on-chain-id",
	version: "0.0.0-pr1640-20241008075233",
	author: "Talisman",
	homepage: "https://talisman.xyz",
	license: "GPL-3.0-or-later",
	publishConfig: {
		access: "public"
	},
	repository: {
		directory: "packages/on-chain-id",
		type: "git",
		url: "https://github.com/talismansociety/talisman.git"
	},
	main: "dist/talismn-on-chain-id.cjs.js",
	module: "dist/talismn-on-chain-id.esm.js",
	files: [
		"/dist"
	],
	engines: {
		node: ">=18"
	},
	scripts: {
		test: "jest",
		lint: "eslint src --max-warnings 0",
		clean: "rm -rf dist .turbo node_modules"
	},
	dependencies: {
		"@azns/resolver-core": "https://gitpkg.vercel.app/api/pkg.tgz?url=TalismanSociety%2Fazero-id-resolver%2Fpackages%2Fresolver-core&commit=main",
		"@talismn/balances": "workspace:*",
		"@talismn/util": "workspace:*",
		anylogger: "^1.0.11"
	},
	devDependencies: {
		"@polkadot/api": "^10.9.1",
		"@polkadot/api-contract": "^10.9.1",
		"@polkadot/types": "^10.9.1",
		"@talismn/eslint-config": "workspace:*",
		"@talismn/tsconfig": "workspace:*",
		"@types/jest": "^27.5.1",
		eslint: "^8.52.0",
		jest: "^29.7.0",
		"ts-jest": "^29.1.1",
		typescript: "^5.2.2"
	},
	peerDependencies: {
		"@polkadot/api": "10.x",
		"@polkadot/api-contract": "10.x",
		"@polkadot/types": "10.x"
	},
	eslintConfig: {
		root: true,
		"extends": [
			"@talismn/eslint-config/base"
		]
	}
};

var log = anylogger(packageJson.name);

/**
 * Looks up the on-chain identifiers for some addresses.
 * Supports ENS and AZNS.
 */
const lookupAddresses = async (config, addresses) => {
  const onChainIds = new Map(addresses.map(address => [address, null]));
  const [aznsDomains, ensDomains] = await Promise.all([lookupAznsAddresses(config, addresses), lookupEnsAddresses(config, addresses)]);
  aznsDomains.forEach((domain, address) => {
    if (!domain) return;
    onChainIds.set(address, domain);
  });
  ensDomains.forEach((domain, address) => {
    if (!domain) return;
    onChainIds.set(address, domain);
  });
  return onChainIds;
};

/**
 * Looks up the on-chain AZNS domains for some addresses.
 */
const lookupAznsAddresses = async (config, addresses) => {
  const onChainIds = new Map(addresses.map(address => [address, null]));
  if (!config.chainConnectors.substrate) {
    log.warn(`Could not find Substrate chainConnector in OnChainId::lookupAznsAddresses`);
    return onChainIds;
  }
  const provider = config.chainConnectors.substrate.asProvider(config.chainIdAlephZero);
  const customApi = new ApiPromise({
    provider,
    registry: config.registryAlephZero
  });
  await customApi.isReady;
  const results = await Promise.allSettled(addresses.map(async address => {
    // no need to do AZNS lookup for ethereum addresses
    if (isEthereumAddress(address)) return;
    try {
      const result = await resolveAddressToDomain(address, {
        chainId: config.aznsSupportedChainIdAlephZero,
        customApi
      });
      if (result.error) throw result.error;
      const {
        primaryDomain: domain
      } = result;
      domain !== null && onChainIds.set(address, domain);
    } catch (cause) {
      throw new Error(`Failed to resolve AZNS domain for address '${address}': ${String(cause)}`, {
        cause
      });
    }
  }));
  results.forEach(result => result.status === "rejected" && log.warn(result.reason));
  return onChainIds;
};

/**
 * Looks up the on-chain ENS domains for some addresses.
 */
const lookupEnsAddresses = async (config, addresses) => {
  const onChainIds = new Map(addresses.map(address => [address, null]));
  const client = await config.chainConnectors.evm?.getPublicClientForEvmNetwork(config.networkIdEthereum);
  if (!client) {
    log.warn(`Could not find Ethereum client in OnChainId::lookupEnsAddresses`);
    return onChainIds;
  }
  const results = await Promise.allSettled(addresses.map(async address => {
    // no need to do ENS lookup for polkadot addresses
    if (!isEthereumAddress(address)) return;
    try {
      const domain = await client.getEnsName({
        address
      });
      domain !== null && onChainIds.set(address, domain);
    } catch (cause) {
      throw new Error(`Failed to resolve ENS domain for address '${address}': ${String(cause)}`, {
        cause
      });
    }
  }));
  results.forEach(result => result.status === "rejected" && log.warn(result.reason));
  return onChainIds;
};

/** dot separated string with `azero` or `tzero` suffix e.g. `fontbois.tzero` or `talisman.tzero` */
const isPotentialAzns = name => typeof name === "string" && /^.+\.[ta]zero$/.test(name);

/** dot separated string e.g. `ens.eth` or `hello.lol`, but not ending with `.azero` nor `.tzero` */
const isPotentialEns = name => typeof name === "string" && /^.+\.(?![ta]zero).+$/.test(name);

const resolveNames = async (config, names) => {
  const resolvedNames = new Map(names.map(name => [name, null]));
  const [aznsNames, ensNames] = await Promise.all([resolveAznsNames(config, names), resolveEnsNames(config, names)]);
  aznsNames.forEach((lookup, name) => {
    if (!lookup) return;
    const [address, lookupType] = lookup;
    if (!address) return;
    resolvedNames.set(name, [address, lookupType]);
  });
  ensNames.forEach((lookup, name) => {
    if (!lookup) return;
    const [address, lookupType] = lookup;
    if (!address) return;
    resolvedNames.set(name, [address, lookupType]);
  });
  return resolvedNames;
};

/**
 * Looks up the addresses for some azns (azero.id) domains.
 */
const resolveAznsNames = async (config, names) => {
  const resolvedNames = new Map(names.map(name => [name, null]));
  if (names.every(name => !isPotentialAzns(name))) return resolvedNames;
  if (!config.chainConnectors.substrate) {
    log.warn(`Could not find Substrate chainConnector in OnChainId::resolveAznsNames`);
    return resolvedNames;
  }
  const provider = config.chainConnectors.substrate.asProvider(config.chainIdAlephZero);
  const customApi = new ApiPromise({
    provider,
    registry: config.registryAlephZero
  });
  await customApi.isReady;
  const results = await Promise.allSettled(names.map(async name => {
    if (!isPotentialAzns(name)) return;
    try {
      const result = await resolveDomainToAddress(name, {
        chainId: config.aznsSupportedChainIdAlephZero,
        customApi
      });
      if (result.error) throw result.error;
      const {
        address
      } = result;
      address !== null && resolvedNames.set(name, [address, "azns"]);
    } catch (cause) {
      throw new Error(`Failed to resolve address for azns domain '${name}'`, {
        cause
      });
    }
  }));
  results.forEach(result => result.status === "rejected" && log.warn(result.reason));
  customApi.disconnect();
  return resolvedNames;
};

/**
 * Looks up the addresses for some ens (ens.domains) domains.
 */
const resolveEnsNames = async (config, names) => {
  const resolvedNames = new Map(names.map(name => [name, null]));
  if (names.every(name => !isPotentialEns(name))) return resolvedNames;
  const client = await config.chainConnectors.evm?.getPublicClientForEvmNetwork(config.networkIdEthereum);
  if (!client) {
    log.warn(`Could not find Ethereum client in OnChainId::resolveNames`);
    return resolvedNames;
  }
  const results = await Promise.allSettled(names.map(async name => {
    if (!isPotentialEns(name)) return;
    try {
      const address = await Promise.race([
      // this hangs forever in some cases (ex: try agyle.e first, then agyle.et => hangs) - couldn't explain it
      client.getEnsAddress({
        name
      }), throwAfter(10_000, "Timeout")]);
      address !== null && resolvedNames.set(name, [address, "ens"]);
    } catch (cause) {
      throw new Error(`Failed to resolve address for ens domain '${name}'`, {
        cause
      });
    }
  }));
  results.forEach(result => result.status === "rejected" && log.warn(result.reason));
  return resolvedNames;
};

class OnChainId {
  #config;
  constructor(config) {
    this.#config = {
      ...config,
      chainIdAlephZero: config.chainIdAlephZero ?? "aleph-zero",
      aznsSupportedChainIdAlephZero: config.aznsSupportedChainIdAlephZero ?? "alephzero",
      networkIdEthereum: config.networkIdEthereum ?? "1"
    };
  }
  resolveNames = (...args) => resolveNames(this.#config, ...args);
  resolveEnsNames = (...args) => resolveEnsNames(this.#config, ...args);
  resolveAznsNames = (...args) => resolveAznsNames(this.#config, ...args);
  lookupAddresses = (...args) => lookupAddresses(this.#config, ...args);
  lookupAznsAddresses = (...args) => lookupAznsAddresses(this.#config, ...args);
  lookupEnsAddresses = (...args) => lookupEnsAddresses(this.#config, ...args);
}

export { OnChainId, isPotentialAzns, isPotentialEns };
